import { motion } from "framer-motion";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../config/api";
import { MapRevealSection } from "../../map/components/MapRevealSection";
import { ErrorState } from "../../shared/components";
import { DEVICE, VIEWPORT } from "../../shared/constants/ui";
import { useOrientationLock } from "../../shared/hooks";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";
import { CollectionRail } from "./components/CollectionRail";
import { ContinueWatchingRail } from "./components/ContinueWatchingRail";
import { FeaturedHeroRail } from "./components/FeaturedHeroRail";
import { NewReleasesRail } from "./components/NewReleasesRail";
import type { CollectionInfo, CollectionResponse, ContentRail, ContentRailsProps } from "./types";

function ContentRails({ rails, transitionTo, onVignetteChange }: Readonly<ContentRailsProps>): ReactElement {
  return (
    <div className="space-y-12">
      {rails.map((r) => {
        // Render MapRevealSection for MAP_REVEAL template
        if (r.template === "MAP_REVEAL" && r.config) {
          const targetOpacity = r.config.vignetteOpacity ?? 0.2;
          return (
            <MapRevealSection
              key={r.id}
              title={r.config.title ?? "Explore World Cinema"}
              subtitle={r.config.subtitle ?? "Discover films by location"}
              buttonText={r.config.buttonText ?? "Browse Map"}
              buttonLink={r.config.buttonLink ?? "/home/browse"}
              transitionTo={transitionTo}
              onHoverChange={(isHovered) => {
                // Fade the fixed vignette when hovering
                onVignetteChange(isHovered ? targetOpacity : 1);
              }}
            />
          );
        }

        // Render PROVIDER template collections with providers
        if (r.template === "PROVIDER") {
          return (
            <CollectionRail
              key={r.id}
              title={r.name}
              items={[]}
              providers={r.providers ?? []}
              cardType="SQUARE"
              loading={false}
              transitionTo={transitionTo}
            />
          );
        }

        // Default to CollectionRail for RAIL template
        return (
          <CollectionRail
            key={r.id}
            title={r.name}
            items={r.items}
            cardType={r.cardType}
            loading={false} // Rails are pre-loaded before ContentRails renders
            transitionTo={transitionTo}
          />
        );
      })}
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
export function HomePage(): ReactElement {
  const [rails, setRails] = useState<ContentRail[]>([]);
  const [skeletonRails, setSkeletonRails] = useState<
    Array<{ id: string; name: string; cardType: "VERTICAL" | "HORIZONTAL" | "SQUARE" }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vignetteOpacity, setVignetteOpacity] = useState(1);

  // Use transition navigation for smooth Home → Title/Player transitions
  const { isTransitioning, transitionTo, supportsViewTransitions } = useTransitionNavigation({
    onTransitionStart: () => console.log("🎬 Starting Home transition"),
    onNavigationComplete: () => console.log("✅ Navigated from Home"),
  });

  // Lock to portrait orientation on touch devices (phones/tablets)
  const { width, height } = VIEWPORT.getDimensions();
  const isTouchDevice = DEVICE.isTouchOptimized(width, height);
  useOrientationLock("portrait", isTouchDevice);

  useEffect(() => {
    // prettier-ignore
    async function loadCollections(): Promise<void> { // NOSONAR
      try {
        console.log("🔍 Starting to load collections...");

        // Fetch all collections metadata
        const collectionsResponse = await apiCall("/v1/collections");
        if (!collectionsResponse.ok) {
          throw new Error(`API Error: ${collectionsResponse.status} ${collectionsResponse.statusText}`);
        }

        const collections = (await collectionsResponse.json()) as CollectionInfo[];
        console.log("✅ Collections loaded:", collections.length);

        // Immediately set skeleton rails with correct cardType
        const sortedCollections = [...collections]
          .filter((c) => c.key !== "featured" && c.key !== "new-releases")
          .sort((a, b) => a.sort - b.sort);

        setSkeletonRails(
          sortedCollections.map((c) => ({
            id: c.key,
            name: c.name,
            cardType: c.cardType,
          })),
        );

        // Fetch items for each collection
        const railsData: ContentRail[] = [];
        for (const collection of sortedCollections) {
          try {
            // MAP_REVEAL collections don't need items
            if (collection.template === "MAP_REVEAL") {
              railsData.push({
                id: collection.key,
                name: collection.name,
                items: [],
                cardType: collection.cardType,
                template: collection.template,
                config: collection.config,
              });
              continue;
            }

            // Fetch items for RAIL or PROVIDER template collections
            const response = await apiCall(`/v1/collections/${collection.key}`);
            if (response.ok) {
              const data = (await response.json()) as CollectionResponse;

              // PROVIDER collections use providers array
              if (collection.template === "PROVIDER") {
                if (data.providers && data.providers.length > 0) {
                  railsData.push({
                    id: collection.key,
                    name: collection.name,
                    items: [],
                    providers: data.providers,
                    cardType: "SQUARE", // PROVIDER collections always use SQUARE
                    template: "PROVIDER",
                    config: collection.config,
                  });
                }
                continue;
              }

              // Title-based collections
              if (data.items.length > 0) {
                railsData.push({
                  id: collection.key,
                  name: collection.name,
                  items: data.items.map((item) => item.title),
                  cardType: collection.cardType,
                  template: collection.template ?? "RAIL",
                  config: collection.config,
                });
              }
            }
          } catch (err) {
            console.warn(`⚠️ Failed to load collection ${collection.key}:`, err);
          }
        }

        console.log("✅ Rails loaded successfully:", railsData.length);
        setRails(railsData);
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load collections:", err);
        if (err instanceof Error) {
          console.error("Error details:", err.message, err.stack);
        }
        setError("Failed to load content");
        setLoading(false);
      }
    }

    void loadCollections();
  }, []);

  // Determine which transition to use based on previous route
  const getVariant = () => {
    // For now, we'll detect if this is initial load or navigation
    const referrer = document.referrer;
    if (referrer.includes("/sign-in")) {
      return pageTransitions.scaleDown;
    }
    return pageTransitions.cinematicFade;
  };

  // Show error only (no skeleton during loading for smooth transitions)
  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <motion.div
      className="relative min-h-screen content-rails"
      {...(supportsViewTransitions
        ? {}
        : {
            initial: "initial",
            animate: isTransitioning ? "exit" : "animate",
            exit: "exit",
            variants: getVariant(),
          })}
    >
      {/* Mapbox satellite background now renders in RootLayout - shows through this page */}

      {/* Fixed vignette - use inset-0 to avoid being affected by transformed ancestors (Framer Motion)
          Some browsers (Safari on macOS) treat fixed elements inside transformed parents as
          positioned relative to that parent. Using `inset-0` ensures the vignette covers the
          viewport regardless of ancestor transforms. */}
      <div
        className={`
          fixed inset-0 pointer-events-none z-[1]
          transition-opacity delay-200 duration-1000 ease-in-out
        `}
        style={{ opacity: vignetteOpacity }}
      >
        <div
          className="w-full h-full"
          style={{
            background: "radial-gradient(ellipse at center, rgba(15, 15, 15, 0.5) 0%, #000000 70%)",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      </div>

      {/* Content layer */}
      <div className="relative z-10 pt-2">
        <FeaturedHeroRail transitionTo={transitionTo} />

        {/* Rails container */}
        <div className="relative z-20 space-y-12 pb-20">
          <ContinueWatchingRail transitionTo={transitionTo} />
          <NewReleasesRail transitionTo={transitionTo} />

          {/* Show skeleton rails while loading with correct cardType, then real content */}
          {loading ? (
            <div className="space-y-12">
              {skeletonRails.map((skeleton) => (
                <CollectionRail
                  key={skeleton.id}
                  title={skeleton.name}
                  items={[]}
                  cardType={skeleton.cardType}
                  loading={true}
                  transitionTo={transitionTo}
                />
              ))}
            </div>
          ) : (
            <ContentRails rails={rails} transitionTo={transitionTo} onVignetteChange={setVignetteOpacity} />
          )}
        </div>
      </div>
    </motion.div>
  );
}
